import React, { useState, useCallback, useRef, type DragEvent, type ChangeEvent } from 'react';

const ALLOWED_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv'];
const MAX_FILE_SIZE = 2_000_000_000; // 2 GB

interface UploadZoneProps {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
}

/**
 * Zona di drop per il caricamento di video.
 * Accetta drag-and-drop e click per selezionare un file.
 * Valida estensione e dimensione prima di chiamare onFileSelect.
 */
const UploadZone: React.FC<UploadZoneProps> = ({ onFileSelect, disabled = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Formato non supportato (${ext}). Usa: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File troppo grande (${(file.size / 1e9).toFixed(1)} GB). Massimo: 2 GB`;
    }
    if (file.size === 0) {
      return 'Il file è vuoto';
    }
    return null;
  }, []);

  const handleFile = useCallback((file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    onFileSelect(file);
  }, [validateFile, onFileSelect]);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
    // Reset input per permettere la riselezione dello stesso file
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const borderColor = isDragging ? '#3b82f6' : error ? '#ef4444' : '#94a3b8';
  const backgroundColor = isDragging ? '#eff6ff' : disabled ? '#f1f5f9' : '#ffffff';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        style={{
          border: `2px dashed ${borderColor}`,
          borderRadius: '12px',
          padding: '2.5rem 2rem',
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor,
          transition: 'all 0.2s',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska"
          onChange={handleInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke={disabled ? '#94a3b8' : '#3b82f6'}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginBottom: '1rem' }}
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <p style={{ margin: 0, fontWeight: 600, color: disabled ? '#94a3b8' : '#1e293b' }}>
          {isDragging ? 'Rilascia il video qui' : 'Trascina un video o clicca per caricare'}
        </p>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#64748b' }}>
          MP4, MOV, AVI, MKV — Massimo 2 GB
        </p>
      </div>
      {error && (
        <div
          style={{
            color: '#dc2626',
            fontSize: '0.85rem',
            padding: '0.5rem',
            backgroundColor: '#fee2e2',
            borderRadius: '6px',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default UploadZone;